/**
 * src/store/slices/organisationSlice.js
 *
 * Admin-only organisation state. Mirrors projectSlice: per-operation loading
 * and error flags rather than one global isLoading, so a create failure can
 * never blank the list that is already on screen.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import organisationService from "../../services/organisationService";

const extractError = (error) =>
  error?.response?.data?.message || error?.message || "Something went wrong.";

// ── Thunks ──────────────────────────────────────────────────────────

export const fetchOrganisations = createAsyncThunk(
  "organisation/fetchOrganisations",
  async (_, { rejectWithValue }) => {
    try {
      const data = await organisationService.getOrganisations();
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchOrganisationById = createAsyncThunk(
  "organisation/fetchOrganisationById",
  async (organisationId, { rejectWithValue }) => {
    try {
      const data = await organisationService.getOrganisationById(organisationId);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const createOrganisation = createAsyncThunk(
  "organisation/createOrganisation",
  async (payload, { rejectWithValue }) => {
    try {
      const data = await organisationService.createOrganisation(payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const updateOrganisation = createAsyncThunk(
  "organisation/updateOrganisation",
  async ({ organisationId, ...payload }, { rejectWithValue }) => {
    try {
      const data = await organisationService.updateOrganisation(
        organisationId,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const addProjectToOrganisation = createAsyncThunk(
  "organisation/addProject",
  async ({ organisationId, projectId }, { rejectWithValue }) => {
    try {
      const data = await organisationService.addProjectToOrganisation(
        organisationId,
        projectId,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const removeProjectFromOrganisation = createAsyncThunk(
  "organisation/removeProject",
  async ({ organisationId, projectId }, { rejectWithValue }) => {
    try {
      await organisationService.removeProjectFromOrganisation(
        organisationId,
        projectId,
      );
      return projectId;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const deleteOrganisation = createAsyncThunk(
  "organisation/deleteOrganisation",
  async ({ organisationId, mode }, { rejectWithValue }) => {
    try {
      await organisationService.deleteOrganisation(organisationId, mode);
      return organisationId;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchUnassignedProjects = createAsyncThunk(
  "organisation/fetchUnassignedProjects",
  async (_, { rejectWithValue }) => {
    try {
      const data = await organisationService.getUnassignedProjects();
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const fetchAvailableClients = createAsyncThunk(
  "organisation/fetchAvailableClients",
  async (organisationId, { rejectWithValue }) => {
    try {
      const data = await organisationService.getAvailableClients(organisationId);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const addExistingClient = createAsyncThunk(
  "organisation/addExistingClient",
  async ({ organisationId, userId }, { rejectWithValue }) => {
    try {
      const data = await organisationService.addExistingClient(
        organisationId,
        userId,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

export const inviteClient = createAsyncThunk(
  "organisation/inviteClient",
  async ({ organisationId, ...payload }, { rejectWithValue }) => {
    try {
      const data = await organisationService.inviteClient(
        organisationId,
        payload,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(extractError(error));
    }
  },
);

// ── Initial State ───────────────────────────────────────────────────

const initialState = {
  organisations: [],
  selectedOrganisation: null,
  unassignedProjects: [],
  availableClients: [],
  isFetchingAvailableClients: false,
  isAddingExistingClient: false,
  addExistingClientError: null,

  isFetchingOrganisations: false,
  isFetchingOrganisation: false,
  isCreatingOrganisation: false,
  isUpdatingOrganisation: false,
  isAddingProject: false,
  isRemovingProject: false,
  isDeletingOrganisation: false,
  isFetchingUnassigned: false,
  isInvitingClient: false,

  organisationsError: null,
  organisationError: null,
  createOrganisationError: null,
  updateOrganisationError: null,
  addProjectError: null,
  removeProjectError: null,
  deleteOrganisationError: null,
  inviteClientError: null,

  createOrganisationSuccess: false,
  inviteClientSuccess: false,
};

// ── Slice ───────────────────────────────────────────────────────────

const organisationSlice = createSlice({
  name: "organisation",
  initialState,

  reducers: {
    clearSelectedOrganisation: (state) => {
      state.selectedOrganisation = null;
      state.organisationError = null;
    },
    resetCreateOrganisation: (state) => {
      state.isCreatingOrganisation = false;
      state.createOrganisationError = null;
      state.createOrganisationSuccess = false;
    },
    resetInviteClient: (state) => {
      state.isInvitingClient = false;
      state.inviteClientError = null;
      state.inviteClientSuccess = false;
    },
    clearOrganisationErrors: (state) => {
      state.organisationsError = null;
      state.organisationError = null;
      state.createOrganisationError = null;
      state.updateOrganisationError = null;
      state.addProjectError = null;
      state.removeProjectError = null;
      state.deleteOrganisationError = null;
      state.inviteClientError = null;
    },
  },

  extraReducers: (builder) => {
    // ── fetchOrganisations ────────────────────
    builder
      .addCase(fetchOrganisations.pending, (state) => {
        state.isFetchingOrganisations = true;
        state.organisationsError = null;
      })
      .addCase(fetchOrganisations.fulfilled, (state, action) => {
        state.isFetchingOrganisations = false;
        state.organisations = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchOrganisations.rejected, (state, action) => {
        state.isFetchingOrganisations = false;
        state.organisationsError = action.payload;
      });

    // ── fetchOrganisationById ─────────────────
    builder
      .addCase(fetchOrganisationById.pending, (state) => {
        state.isFetchingOrganisation = true;
        state.organisationError = null;
        state.selectedOrganisation = null;
      })
      .addCase(fetchOrganisationById.fulfilled, (state, action) => {
        state.isFetchingOrganisation = false;
        state.selectedOrganisation = action.payload;
      })
      .addCase(fetchOrganisationById.rejected, (state, action) => {
        state.isFetchingOrganisation = false;
        state.organisationError = action.payload;
      });

    // ── createOrganisation ────────────────────
    builder
      .addCase(createOrganisation.pending, (state) => {
        state.isCreatingOrganisation = true;
        state.createOrganisationError = null;
        state.createOrganisationSuccess = false;
      })
      .addCase(createOrganisation.fulfilled, (state, action) => {
        state.isCreatingOrganisation = false;
        state.createOrganisationSuccess = true;
        // Newest first, matching the server's sort.
        if (action.payload?._id) {
          state.organisations = [action.payload, ...state.organisations];
        }
      })
      .addCase(createOrganisation.rejected, (state, action) => {
        state.isCreatingOrganisation = false;
        state.createOrganisationError = action.payload;
      });

    // ── updateOrganisation ────────────────────
    builder
      .addCase(updateOrganisation.pending, (state) => {
        state.isUpdatingOrganisation = true;
        state.updateOrganisationError = null;
      })
      .addCase(updateOrganisation.fulfilled, (state, action) => {
        state.isUpdatingOrganisation = false;
        const updated = action.payload;
        if (!updated?._id) return;
        state.organisations = state.organisations.map((o) =>
          o._id === updated._id ? { ...o, ...updated } : o,
        );
        if (state.selectedOrganisation?._id === updated._id) {
          state.selectedOrganisation = {
            ...state.selectedOrganisation,
            ...updated,
          };
        }
      })
      .addCase(updateOrganisation.rejected, (state, action) => {
        state.isUpdatingOrganisation = false;
        state.updateOrganisationError = action.payload;
      });

    // ── addProjectToOrganisation ──────────────
    builder
      .addCase(addProjectToOrganisation.pending, (state) => {
        state.isAddingProject = true;
        state.addProjectError = null;
      })
      .addCase(addProjectToOrganisation.fulfilled, (state) => {
        state.isAddingProject = false;
      })
      .addCase(addProjectToOrganisation.rejected, (state, action) => {
        state.isAddingProject = false;
        state.addProjectError = action.payload;
      });

    // ── removeProjectFromOrganisation ─────────
    builder
      .addCase(removeProjectFromOrganisation.pending, (state) => {
        state.isRemovingProject = true;
        state.removeProjectError = null;
      })
      .addCase(removeProjectFromOrganisation.fulfilled, (state, action) => {
        state.isRemovingProject = false;
        if (state.selectedOrganisation?.projects) {
          state.selectedOrganisation.projects =
            state.selectedOrganisation.projects.filter(
              (p) => p._id !== action.payload,
            );
        }
      })
      .addCase(removeProjectFromOrganisation.rejected, (state, action) => {
        state.isRemovingProject = false;
        state.removeProjectError = action.payload;
      });

    // ── deleteOrganisation ────────────────────
    builder
      .addCase(deleteOrganisation.pending, (state) => {
        state.isDeletingOrganisation = true;
        state.deleteOrganisationError = null;
      })
      .addCase(deleteOrganisation.fulfilled, (state, action) => {
        state.isDeletingOrganisation = false;
        state.organisations = state.organisations.filter(
          (o) => o._id !== action.payload,
        );
        if (state.selectedOrganisation?._id === action.payload) {
          state.selectedOrganisation = null;
        }
      })
      .addCase(deleteOrganisation.rejected, (state, action) => {
        state.isDeletingOrganisation = false;
        state.deleteOrganisationError = action.payload;
      });

    // ── fetchUnassignedProjects ───────────────
    builder
      .addCase(fetchUnassignedProjects.pending, (state) => {
        state.isFetchingUnassigned = true;
      })
      .addCase(fetchUnassignedProjects.fulfilled, (state, action) => {
        state.isFetchingUnassigned = false;
        state.unassignedProjects = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchUnassignedProjects.rejected, (state) => {
        state.isFetchingUnassigned = false;
        state.unassignedProjects = [];
      });

    // ── fetchAvailableClients ─────────────────
    builder
      .addCase(fetchAvailableClients.pending, (state) => {
        state.isFetchingAvailableClients = true;
      })
      .addCase(fetchAvailableClients.fulfilled, (state, action) => {
        state.isFetchingAvailableClients = false;
        state.availableClients = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchAvailableClients.rejected, (state) => {
        state.isFetchingAvailableClients = false;
        state.availableClients = [];
      });

    // ── addExistingClient ─────────────────────
    builder
      .addCase(addExistingClient.pending, (state) => {
        state.isAddingExistingClient = true;
        state.addExistingClientError = null;
      })
      .addCase(addExistingClient.fulfilled, (state) => {
        state.isAddingExistingClient = false;
      })
      .addCase(addExistingClient.rejected, (state, action) => {
        state.isAddingExistingClient = false;
        state.addExistingClientError = action.payload;
      });

    // ── inviteClient ──────────────────────────
    builder
      .addCase(inviteClient.pending, (state) => {
        state.isInvitingClient = true;
        state.inviteClientError = null;
        state.inviteClientSuccess = false;
      })
      .addCase(inviteClient.fulfilled, (state) => {
        state.isInvitingClient = false;
        state.inviteClientSuccess = true;
      })
      .addCase(inviteClient.rejected, (state, action) => {
        state.isInvitingClient = false;
        state.inviteClientError = action.payload;
      });
  },
});

export const {
  clearSelectedOrganisation,
  resetCreateOrganisation,
  resetInviteClient,
  clearOrganisationErrors,
} = organisationSlice.actions;

export default organisationSlice.reducer;
